// AI Quality Analysis — button-triggered run, results stored in Postgres,
// and a time-series chart of snapshot history.

import { useCallback, useEffect, useState } from 'react'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { PULSE_COLORS } from './pulseConfig.js'

function fmtPct(v) {
    if (v == null) return '—'
    return `${(v * 100).toFixed(1)}%`
}
function fmt(v, digits = 2) {
    if (v == null) return '—'
    return Number(v).toFixed(digits)
}
function fmtDate(iso) {
    if (!iso) return ''
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
function fmtDateTime(iso) {
    if (!iso) return ''
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function AIQualityAnalysis() {
    const [snapshots, setSnapshots] = useState([])
    const [loading, setLoading] = useState(true)
    const [running, setRunning] = useState(false)
    const [error, setError] = useState(null)
    const [lastRun, setLastRun] = useState(null)

    const refresh = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await fetch('/api/pulse/ai-analysis/snapshots')
            if (!res.ok) throw new Error(`API returned ${res.status}`)
            const json = await res.json()
            setSnapshots(json.snapshots ?? [])
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load snapshots')
        } finally {
            setLoading(false)
        }
    }, [])

    const runAnalysis = useCallback(async () => {
        setRunning(true)
        setError(null)
        try {
            const res = await fetch('/api/pulse/ai-analysis/run', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({}),
            })
            const json = await res.json()
            if (!res.ok) throw new Error(json?.error ?? `API returned ${res.status}`)
            setLastRun(json)
            await refresh()
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Run failed')
        } finally {
            setRunning(false)
        }
    }, [refresh])

    useEffect(() => {
        refresh()
    }, [refresh])

    const latest = snapshots[0] // ORDER BY run_at DESC

    // Time-series chart data — reverse so X axis is left-to-right oldest→newest
    const chartData = [...snapshots].reverse().map((s) => ({
        runAt: s.run_at,
        runLabel: fmtDate(s.run_at),
        aiRate: Number(s.ai_rate) * 100,
        nonAiRate: Number(s.non_ai_rate) * 100,
        orMain: Number(s.or_main),
    }))

    return (
        <div
            style={{
                background: PULSE_COLORS.cardBg,
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${PULSE_COLORS.border}`,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
                <div>
                    <h3 style={{ color: PULSE_COLORS.text, fontWeight: 600, fontSize: '16px' }}>
                        🤖 AI Quality Analysis
                    </h3>
                    <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', marginTop: '2px', maxWidth: '700px' }}>
                        Per-story analysis: does AI-labeled work produce more linked bugs than non-AI work?
                        Each run pulls only new data, attributes bugs via explicit links + same-epic-within-30d
                        rule, and stores a snapshot. First run is slow; subsequent runs are fast.
                    </p>
                </div>
                <button
                    onClick={runAnalysis}
                    disabled={running}
                    style={{
                        padding: '10px 20px',
                        background: running ? PULSE_COLORS.border : PULSE_COLORS.primary,
                        color: '#fff',
                        fontSize: '13px',
                        fontWeight: 600,
                        borderRadius: '8px',
                        border: 'none',
                        cursor: running ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {running ? '⏳ Running…' : snapshots.length === 0 ? '▶ Run first analysis' : '↻ Run incremental update'}
                </button>
            </div>

            {error && (
                <div
                    style={{
                        padding: '12px',
                        marginBottom: '16px',
                        background: '#7f1d1d33',
                        border: '1px solid #b91c1c',
                        borderRadius: '8px',
                        color: '#FECDD3',
                        fontSize: '13px',
                    }}
                >
                    {error}
                </div>
            )}

            {lastRun && (
                <div
                    style={{
                        padding: '10px 14px',
                        marginBottom: '16px',
                        background: PULSE_COLORS.bgInner,
                        border: `1px solid ${PULSE_COLORS.border}`,
                        borderRadius: '8px',
                        fontSize: '12px',
                        color: PULSE_COLORS.textMuted,
                    }}
                >
                    Last run: <strong style={{ color: PULSE_COLORS.text }}>{lastRun.incremental ? 'incremental' : 'full'}</strong>
                    {' · '}fetched {lastRun.fetched.stories} new stories, {lastRun.fetched.bugs} new bugs
                    {' · '}cached: {lastRun.cached.stories} stories, {lastRun.cached.bugs} bugs
                    {' · '}{fmt(lastRun.analysis.fetchSeconds, 1)}s
                </div>
            )}

            {!loading && snapshots.length === 0 && !error && (
                <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', margin: '24px 0' }}>
                    No analysis runs yet. Click the button to do your first run — first run will pull ~6 months
                    of Jira data and may take 30–60 seconds.
                </p>
            )}

            {latest && (
                <>
                    <SummaryGrid s={latest} />
                    <PodTable s={latest} />
                    {chartData.length >= 2 && <TrendChart data={chartData} />}
                </>
            )}
        </div>
    )
}

function SummaryGrid({ s }) {
    const orVal = Number(s.or_main)
    const ciLow = Number(s.ci_low_main)
    const ciHigh = Number(s.ci_high_main)
    const ciIncludesOne = ciLow <= 1 && ciHigh >= 1
    const orColor = ciIncludesOne ? PULSE_COLORS.textMuted : orVal > 1 ? '#f87171' : '#34d399'

    const falsOr = Number(s.or_falsification)
    const falsCiLow = Number(s.ci_low_falsification)
    const falsCiHigh = Number(s.ci_high_falsification)
    const falsCiIncludesOne = falsCiLow <= 1 && falsCiHigh >= 1

    return (
        <>
            <div
                style={{
                    fontSize: '11px',
                    color: PULSE_COLORS.textMuted,
                    marginBottom: '8px',
                }}
            >
                Latest snapshot: {fmtDateTime(s.run_at)} — n={s.n_total} stories ({s.n_ai} AI, {s.n_non_ai} non-AI),
                window {s.window_days}d, attribution {s.attribution_window_days}d
            </div>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '12px',
                    marginBottom: '20px',
                }}
            >
                <Card title="AI bug rate" value={fmtPct(s.ai_rate)} sub={`${s.positives_ai} / ${s.n_ai} stories`} color="#06B6D4" />
                <Card title="Non-AI bug rate" value={fmtPct(s.non_ai_rate)} sub={`${s.positives_non_ai} / ${s.n_non_ai} stories`} color="#64748B" />
                <Card
                    title="Odds ratio (AI vs non-AI)"
                    value={fmt(orVal)}
                    sub={`95% CI: ${fmt(ciLow)}–${fmt(ciHigh)}`}
                    color={orColor}
                    note={ciIncludesOne ? 'No significant effect' : orVal > 1 ? 'AI WORSE' : 'AI BETTER'}
                />
                <Card
                    title="Falsification OR (pre-window)"
                    value={fmt(falsOr)}
                    sub={`95% CI: ${fmt(falsCiLow)}–${fmt(falsCiHigh)}`}
                    color={falsCiIncludesOne ? '#34d399' : '#f87171'}
                    note={falsCiIncludesOne ? '✓ Null (expected)' : '⚠ Signal'}
                />
            </div>
        </>
    )
}

function Card({ title, value, sub, color, note }) {
    return (
        <div
            style={{
                padding: '14px',
                background: PULSE_COLORS.bgInner,
                border: `1px solid ${PULSE_COLORS.border}`,
                borderRadius: '8px',
            }}
        >
            <div style={{ fontSize: '11px', color: PULSE_COLORS.textMuted, marginBottom: '4px' }}>{title}</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color }}>{value}</div>
            <div style={{ fontSize: '11px', color: PULSE_COLORS.textMuted, marginTop: '2px' }}>{sub}</div>
            {note && (
                <div style={{ fontSize: '10px', color, fontWeight: 600, marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {note}
                </div>
            )}
        </div>
    )
}

function PodTable({ s }) {
    const byPod = s.by_pod ?? {}
    return (
        <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: PULSE_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Per-pod breakdown
            </div>
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ borderBottom: `1px solid ${PULSE_COLORS.border}` }}>
                        <th style={{ textAlign: 'left', padding: '6px 12px 6px 0', color: PULSE_COLORS.textMuted, fontWeight: 500 }}>Pod</th>
                        <th style={{ textAlign: 'right', padding: '6px 12px', color: PULSE_COLORS.textMuted, fontWeight: 500 }}>AI n</th>
                        <th style={{ textAlign: 'right', padding: '6px 12px', color: PULSE_COLORS.textMuted, fontWeight: 500 }}>AI rate</th>
                        <th style={{ textAlign: 'right', padding: '6px 12px', color: PULSE_COLORS.textMuted, fontWeight: 500 }}>Non-AI n</th>
                        <th style={{ textAlign: 'right', padding: '6px 12px', color: PULSE_COLORS.textMuted, fontWeight: 500 }}>Non-AI rate</th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(byPod).map(([pod, d]) => (
                        <tr key={pod} style={{ borderBottom: `1px solid ${PULSE_COLORS.border}` }}>
                            <td style={{ padding: '6px 12px 6px 0', color: PULSE_COLORS.text, fontWeight: 600 }}>{pod}</td>
                            <td style={{ textAlign: 'right', padding: '6px 12px', color: PULSE_COLORS.text }}>{d.n_ai}</td>
                            <td style={{ textAlign: 'right', padding: '6px 12px', color: '#06B6D4', fontWeight: 600 }}>{fmtPct(d.ai_rate)}</td>
                            <td style={{ textAlign: 'right', padding: '6px 12px', color: PULSE_COLORS.text }}>{d.n_non_ai}</td>
                            <td style={{ textAlign: 'right', padding: '6px 12px', color: PULSE_COLORS.textMuted, fontWeight: 600 }}>{fmtPct(d.non_ai_rate)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

function TrendChart({ data }) {
    return (
        <div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: PULSE_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Trend — AI vs non-AI bug rate over time
            </div>
            <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={PULSE_COLORS.border} />
                    <XAxis dataKey="runLabel" tick={{ fill: PULSE_COLORS.textMuted, fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis
                        tick={{ fill: PULSE_COLORS.textMuted, fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => `${v.toFixed(1)}%`}
                        label={{
                            value: 'Bug rate (%)',
                            angle: -90,
                            position: 'insideLeft',
                            offset: 12,
                            style: { fill: PULSE_COLORS.textMuted, fontSize: 11 },
                        }}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: PULSE_COLORS.bgInner,
                            border: `1px solid ${PULSE_COLORS.border}`,
                            borderRadius: '8px',
                            color: PULSE_COLORS.text,
                            fontSize: '12px',
                        }}
                        formatter={(value, name) => [`${Number(value).toFixed(2)}%`, name]}
                    />
                    <Legend
                        formatter={(value) => <span style={{ color: PULSE_COLORS.textMuted, fontSize: '11px' }}>{value}</span>}
                    />
                    <Line type="monotone" dataKey="aiRate" name="AI" stroke="#06B6D4" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="nonAiRate" name="Non-AI" stroke="#94A3B8" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    )
}
